import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { VEHICLE_TYPES, JOB_CATEGORIES } from "../lib/constants";
import { Plus } from "lucide-react";

const EMPTY = { title: "", job_category: "", description: "", pickup_location: "", delivery_location: "", pickup_datetime: "", delivery_deadline: "", goods_type: "", weight: "", pallets: "", truck_type: "", loading_help: false, price: "", request_quote: false, contact_method: "" };

export const PostJobDialog = ({ onSaved, disabled, label = "Post Transport Job", className = "rounded-none bg-foreground hover:bg-foreground/90 text-white" }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/transport-jobs", { ...form, price: form.price ? parseFloat(form.price) : null });
      toast.success("Transport job posted");
      setOpen(false);
      setForm(EMPTY);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} className={className} data-testid="add-job-btn"><Plus className="h-4 w-4 mr-1" /> {label}</Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Post a transport job</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-caps">Title</Label><Input value={form.title} onChange={upd("title")} className="rounded-none mt-1 h-10" placeholder="e.g. Pallet delivery Berlin → Hamburg" data-testid="job-title-input" /></div>
          <div><Label className="label-caps">Job type</Label>
            <Select value={form.job_category} onValueChange={(v) => setForm({ ...form, job_category: v })}>
              <SelectTrigger className="rounded-none mt-1 h-10" data-testid="job-category-input"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{JOB_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Pickup location</Label><Input value={form.pickup_location} onChange={upd("pickup_location")} className="rounded-none mt-1 h-10" data-testid="job-pickup-input" /></div>
            <div><Label className="label-caps">Delivery location</Label><Input value={form.delivery_location} onChange={upd("delivery_location")} className="rounded-none mt-1 h-10" data-testid="job-delivery-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Pickup date/time</Label><Input value={form.pickup_datetime} onChange={upd("pickup_datetime")} placeholder="e.g. 12 Jun, 09:00" className="rounded-none mt-1 h-10" data-testid="job-pickupdate-input" /></div>
            <div><Label className="label-caps">Delivery deadline</Label><Input value={form.delivery_deadline} onChange={upd("delivery_deadline")} placeholder="e.g. 13 Jun" className="rounded-none mt-1 h-10" data-testid="job-deadline-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="label-caps">Goods type</Label><Input value={form.goods_type} onChange={upd("goods_type")} className="rounded-none mt-1 h-10" data-testid="job-goods-input" /></div>
            <div><Label className="label-caps">Weight</Label><Input value={form.weight} onChange={upd("weight")} placeholder="e.g. 500kg" className="rounded-none mt-1 h-10" data-testid="job-weight-input" /></div>
            <div><Label className="label-caps">Pallets/items</Label><Input value={form.pallets} onChange={upd("pallets")} className="rounded-none mt-1 h-10" data-testid="job-pallets-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Truck type needed</Label>
              <Select value={form.truck_type} onValueChange={(v) => setForm({ ...form, truck_type: v })}>
                <SelectTrigger className="rounded-none mt-1 h-10" data-testid="job-trucktype-input"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-caps">Contact method</Label><Input value={form.contact_method} onChange={upd("contact_method")} placeholder="Phone / WhatsApp / platform" className="rounded-none mt-1 h-10" data-testid="job-contact-input" /></div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.loading_help} onChange={(e) => setForm({ ...form, loading_help: e.target.checked })} data-testid="job-loading-input" /> Loading help required</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.request_quote} onChange={(e) => setForm({ ...form, request_quote: e.target.checked })} data-testid="job-quote-input" /> Request a quote</label>
          </div>
          {!form.request_quote && <div><Label className="label-caps">Price / budget (£)</Label><Input type="number" value={form.price} onChange={upd("price")} className="rounded-none mt-1 h-10" data-testid="job-price-input" /></div>}
          <div><Label className="label-caps">Description</Label><Textarea value={form.description} onChange={upd("description")} rows={2} className="rounded-none mt-1" data-testid="job-description-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !form.title || !form.pickup_location || !form.delivery_location} className="rounded-none bg-primary hover:bg-primary/90" data-testid="save-job-btn">{saving ? "Posting…" : "Post job"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
